import * as React from 'react';
import { useState } from 'react';

import { Text } from '@/components/atoms/Text';
import { FileInput } from '@/components/form/FileInput';
import { SvyButton } from '@/components/savory/SvyButton';
import { confirm } from '@/core/asyncActions/confirm';
import { Form } from '@/core/form/Form';
import { computeText } from '@/locales/utils';
import {
    DescriptionList,
    DropzoneContainer,
    TextContainer,
} from '@/pages/MenuList/components/MenuModal/CreateMenuModal/ImportMenuStep/ImportMenuStep.style';
import { getUserToken } from '@/redux/user';
import { ENV } from '@/utils/env';
import { Accept } from 'react-dropzone';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { ErrorMessage, FormControl, FormControls, LogLine, LogsContainer, SuccessMessage } from '../AdminTools.style';

export type EditStepInfoFromStepIdInputs = {
    file: File;
};

const ALLOWED_FILE_TYPES: Accept = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-excel': ['.xls'],
};

const MAX_LOGS = 500;

const EditStepInfoFromStepId: React.FunctionComponent = () => {
    const intl = useIntl();
    const token = useSelector(getUserToken);
    const [logs, setLogs] = useState<string[]>([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isError, setIsError] = useState(false);

    const handleSubmitForm = async ({ file }: EditStepInfoFromStepIdInputs) => {
        const hasConfirmed = await confirm(computeText(intl, 'adminTools.confirmModal.description'));
        if (hasConfirmed) {
            setLogs([]);
            setIsError(false);
            setIsSuccess(false);
            setIsStreaming(true);

            try {
                const formData = new FormData();
                formData.append('file', file);

                const response = await fetch(`${ENV.API_URL}/scripts/edit_step_info_from_step_id`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    body: formData,
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ message: response.statusText }));
                    const errorMessage = errorData?.message || errorData?.error || response.statusText;
                    setLogs((prev) => [...prev, `❌ Erreur: ${errorMessage}`]);
                    return;
                }

                if (!response.body) {
                    throw new Error('No response body');
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                while (true) {
                    const { value, done } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });
                    const parts = buffer.split('\n');
                    buffer = parts.pop() || '';

                    const newLogs = parts.filter((part) => part.trim() && !part.startsWith('{'));
                    if (newLogs.length > 0) {
                        setLogs((prev) => [...prev, ...newLogs].slice(-MAX_LOGS));
                    }
                }

                // Check if there were any errors in the logs
                setLogs((prev) => {
                    const hasErrorLogs = prev.some((log) => log.includes('❌'));
                    if (hasErrorLogs) {
                        setIsError(true);
                    } else {
                        setIsSuccess(true);
                    }
                    return prev;
                });
            } catch (error) {
                console.error('Error:', error);
                setIsError(true);
                // Only show fetch error if we don't already have error logs
                setLogs((prev) => {
                    const hasErrorLogs = prev.some((log) => log.includes('❌'));
                    if (hasErrorLogs) return prev;
                    return [...prev, `❌ Erreur: ${error}`];
                });
            } finally {
                setIsStreaming(false);
            }
        }
    };

    return (
        <Form<EditStepInfoFromStepIdInputs>>
            {({ handleSubmit }) => {
                return (
                    <>
                        <Text type="large" text="brand.adminTools.editStepInfoFromStepId.title" />

                        <TextContainer>
                            <DescriptionList>
                                <FormattedMessage id="brand.adminTools.editStepInfoFromStepId.description0" />
                                <br />
                                <FormattedMessage
                                    id={`brand.adminTools.editStepInfoFromStepId.description1`}
                                    values={{
                                        metabaseLink: (
                                            <a
                                                href={`https://metabase-prod.innovorder.io/question/7148-pour-import-liste-etapes-dun-menu`}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                <FormattedMessage id="file.metabaseLink" />
                                            </a>
                                        ),
                                    }}
                                />
                            </DescriptionList>
                        </TextContainer>
                        <FormControls>
                            <FormControl>
                                <DropzoneContainer>
                                    <React.Suspense>
                                        <FileInput
                                            mode="compact"
                                            accept={ALLOWED_FILE_TYPES}
                                            name="file"
                                            transform={{
                                                output: (file) => file[0],
                                                input: (file: File) => (file ? [file] : []),
                                            }}
                                        />
                                    </React.Suspense>
                                </DropzoneContainer>
                            </FormControl>

                            {logs.length > 0 && (
                                <LogsContainer>
                                    {logs.map((log, index) => (
                                        <LogLine
                                            key={index}
                                            isStreaming={isStreaming && index === logs.length - 1}
                                            isSuccess={false}
                                        >
                                            {log}
                                        </LogLine>
                                    ))}
                                </LogsContainer>
                            )}

                            <SvyButton onClick={handleSubmit(handleSubmitForm)} disabled={isStreaming}>
                                <FormattedMessage id="button.validate" />
                            </SvyButton>
                            {isSuccess && (
                                <SuccessMessage text="brand.adminTools.editStepInfoFromStepId.form.success" />
                            )}
                            {isError && <ErrorMessage text="brand.adminTools.editStepInfoFromStepId.form.error" />}
                        </FormControls>
                    </>
                );
            }}
        </Form>
    );
};

export default EditStepInfoFromStepId;
